import { css, type Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { useField } from "formik";
import { forwardRef, useCallback, useState } from "react";

import { useFieldContext } from "../FormFieldContext";
import { useFieldPlaceholder, useHandleBlur } from "./helpers";

type DynamicProps = {
  size?: "small" | "large";
  minHeight?: string;
  cancelOnEscape?: boolean;
  maxLines?: number;
  expand?: boolean;
  useAfter?: boolean;
};

type FieldTextareaDynamicProps = Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "css"> &
  DynamicProps & {
    onEnterKey?: () => void;
    onEscapeKey?: () => void;
  };

const sharedCss = ({ minHeight, size, useAfter, theme }: DynamicProps & { theme: Theme }) => css`
  overflow: hidden;

  width: 100%;
  max-width: 100%;
  min-height: ${minHeight ? minHeight : "1.5rem"};

  margin: 0;
  border: 1px solid transparent;

  font-family: inherit;
  font-weight: ${size === "large" ? "600" : "400"};
  font-size: ${size === "large" ? theme.fontSizeLarge : theme.fontSize};

  text-align: start;
  line-height: 1.2em;

  white-space: pre-wrap;

  overflow-wrap: anywhere;
  background-color: inherit;

  ${useAfter &&
  `
  &::after {
    display: inline;
    content: "\u{200B}";
  }`}
`;

const DynamicDiv = styled.div<DynamicProps>`
  position: relative;

  display: -webkit-box;

  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ expand, maxLines }) => (!expand ? maxLines : "unset")};

  padding: 2px;

  color: transparent;

  ${sharedCss}
`;

const ClampedDynamicDiv = styled(DynamicDiv)`
  position: absolute;
  top: 0;
  left: 0;

  padding: 2px;
`;

const DynamicTextarea = styled.textarea<DynamicProps>`
  position: absolute;

  left: 0;
  top: 0;

  height: 100%;

  resize: none;
  outline: none;

  padding: 2px;
  border-radius: ${({ theme }) => theme.borderRadius}px;

  opacity: ${({ expand }) => (expand ? 1 : 0)};

  ${sharedCss}

  &:hover:not([readonly]) {
    border-color: ${({ theme }) => theme.outlineSoft};
  }

  &:focus:not([readonly]) {
    border: 1px solid ${({ theme }) => theme.primaryContainer};
    background-color: ${({ theme }) => theme.surface};
  }

  &::placeholder {
    color: ${({ theme }) => theme.generalTextIconVariant};
  }

  &:placeholder-shown {
    opacity: 1;
  }
`;

export const FieldTextareaDynamic = forwardRef<HTMLTextAreaElement, FieldTextareaDynamicProps>((props, ref) => {
  const { cancelOnEscape, onEnterKey, onEscapeKey, ...fieldProps } = props;
  const { readOnly, onBlur, size, minHeight, expand, maxLines = Number.MAX_SAFE_INTEGER } = fieldProps;

  const { controlId, dataTest, name } = useFieldContext();
  const [{ value, onChange }, { initialValue }, { setValue }] = useField(name);

  const [useAfter, setUseAfter] = useState((initialValue as string).endsWith("\n"));

  const fieldPlaceholder = useFieldPlaceholder(name);

  const handleBlur = useHandleBlur();

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!readOnly) event.stopPropagation();

      if (event.key === "Enter" && event.shiftKey) setUseAfter(true);

      if (event.key === "Enter" && !event.shiftKey) {
        event.currentTarget.blur();
        onEnterKey?.();
      }

      if (event.key === "Escape") {
        event.currentTarget.blur();

        if (cancelOnEscape) {
          void setValue(initialValue);
        }

        onEscapeKey?.();
      }
    },
    [cancelOnEscape, initialValue, onEnterKey, onEscapeKey, readOnly, setValue],
  );

  const handleOnBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      onBlur?.(e);
      handleBlur(e);
    },
    [handleBlur, onBlur],
  );

  return (
    <DynamicDiv size={size} minHeight={minHeight} expand={expand} maxLines={maxLines} useAfter={useAfter}>
      {value}
      <DynamicTextarea
        {...fieldProps}
        ref={ref}
        id={controlId}
        name={name}
        placeholder={props.placeholder ?? fieldPlaceholder}
        expand={expand}
        value={value}
        onBlur={handleOnBlur}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        readOnly={readOnly}
        autoComplete="off"
        data-test={`${dataTest ?? name}-textarea`}
      />
      {!expand && (
        <ClampedDynamicDiv size={size} expand={expand} maxLines={maxLines} useAfter={useAfter}>
          {value}
        </ClampedDynamicDiv>
      )}
    </DynamicDiv>
  );
});
FieldTextareaDynamic.displayName = "FieldTextareaDynamic";
